import React, {useCallback, useEffect, useState} from "react";
import {Skeleton, Tag} from "antd";
import {useTranslation} from "react-i18next";
import {APIv1} from "../../../../api";
import "./DeviceHottest.css";

const temperatureGroups = [
    {
        key: "group_1",
        scaleSpan: 39,
        scaleTick: "0°C",
        tone: "cyan",
        labelKey: "analysis.deviceTemperature.groups.cool",
        rangeKey: "analysis.deviceTemperature.ranges.cool",
    },
    {
        key: "group_2",
        scaleSpan: 10,
        scaleTick: "40°C",
        tone: "green",
        labelKey: "analysis.deviceTemperature.groups.comfort",
        rangeKey: "analysis.deviceTemperature.ranges.comfort",
    },
    {
        key: "group_3",
        scaleSpan: 10,
        scaleTick: "50°C",
        tone: "orange",
        labelKey: "analysis.deviceTemperature.groups.hot",
        rangeKey: "analysis.deviceTemperature.ranges.hot",
    },
    {
        key: "group_4",
        scaleSpan: 15,
        scaleTick: "60°C+",
        tone: "red",
        labelKey: "analysis.deviceTemperature.groups.veryHot",
        rangeKey: "analysis.deviceTemperature.ranges.veryHot",
    },
    {
        key: "group_null",
        tone: "geekblue",
        labelKey: "analysis.deviceTemperature.groups.noInfo",
        rangeKey: null,
    },
];

const DeviceHottest = ({onTotalChange}) => {
    const {t} = useTranslation();
    const [userData, setUserData] = useState({});
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await APIv1.get("/analysis/device/hottest/", {
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });

            setData(response.data || {});
        } catch (error) {
            console.error("Error fetching device temperature groups:", error);
        } finally {
            setLoading(false);
        }
    }, [userData.token]);

    useEffect(() => {
        if (!userData.token) return;

        fetchData();
    }, [userData.token, fetchData]);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem("user"));
        if (items) {
            setUserData(items);
        }
    }, [userData.token]);

    const groups = temperatureGroups.map((group) => ({
        ...group,
        count: Number(data[group.key]) || 0,
    }));
    const scaleGroups = groups.filter(({scaleSpan}) => scaleSpan);
    const total = groups.reduce((sum, group) => sum + group.count, 0);
    const getPercent = (count) => (total ? Math.round((count / total) * 100) : 0);

    useEffect(() => {
        onTotalChange?.(loading ? null : total);
    }, [loading, total, onTotalChange]);

    return (
        <div className="analysis__device-hottest">
            <div className="analysis__device-hottest-header">
                <h2>{t("analysis.deviceTemperature.groupTitle")}</h2>
                {!loading && (
                    <span className="analysis__device-hottest-total">
                        {`${total} ${t("analysis.deviceTemperature.subLabel")}`}
                    </span>
                )}
            </div>

            <div
                className="analysis__device-hottest-scale"
                role="img"
                aria-label={groups
                    .map(({
                              labelKey,
                              count
                          }) => `${t(labelKey)}: ${count} ${t("analysis.deviceTemperature.subLabel")}`)
                    .join(", ")}
            >
                {scaleGroups.map(({key, tone, count, scaleSpan, scaleTick}) => (
                    <div className="analysis__device-hottest-scale-step" key={key} style={{flexGrow: scaleSpan}}>
                        <span
                            className={`analysis__device-hottest-segment analysis__device-hottest-segment--${tone}${!loading && count === 0 ? " analysis__device-hottest-segment--empty" : ""}`}
                        >
                            {!loading && count}
                        </span>
                        <span className="analysis__device-hottest-tick">{scaleTick}</span>
                    </div>
                ))}
            </div>

            <div className="analysis__device-hottest-grid">
                {groups.map(({key, tone, labelKey, rangeKey, count}) => (
                    <div
                        className={`analysis__device-hottest-card analysis__device-hottest-card--${tone}`}
                        key={key}
                    >
                        <Tag color={tone} bordered={false} className="analysis__device-hottest-tag">
                            {t(rangeKey || labelKey)}
                        </Tag>
                        {loading ? (
                            <Skeleton.Button active style={{width: 64, height: 18}}/>
                        ) : (
                            <div className="analysis__device-hottest-count">
                                <strong>
                                    <span className="analysis__device-hottest-no">№</span>
                                    {count}
                                </strong>
                                <span>{t("analysis.deviceTemperature.subLabel")}</span>
                            </div>
                        )}
                        <span className="analysis__device-hottest-label">
                            {[
                                rangeKey && t(labelKey),
                                !loading && total > 0 && `${getPercent(count)}%`,
                            ].filter(Boolean).join(" · ") || "\u00a0"}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DeviceHottest;
